import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MarkdownView from "@/components/MarkdownView";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid,
} from "recharts";
import { LayoutDashboard, Clock, Users, FileText, Activity, AlertTriangle, Sparkles, Copy, Target, Mail, Flame, Lock, Check, CalendarRange, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { buildRecapCard } from "@/lib/streakCard";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const localToday = () => { const d = new Date(); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };

const MILESTONE_LABELS = { 7: "One week", 30: "One month", 100: "Century" };

function WeeklyRecap({ recap }) {
  const { t } = useTranslation();
  if (!recap) return null;
  const range = (() => {
    try {
      const s = new Date(recap.start + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
      const e = new Date(recap.end + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
      return `${s} – ${e}`;
    } catch { return ""; }
  })();
  const shareRecap = async () => {
    const text = `${t("dashboard.thisWeek")} — JotBay: ${recap.total_hours}h · ${recap.entry_count} · ${recap.streak}`;
    const url = window.location.origin;
    try {
      const blob = await buildRecapCard(recap);
      const file = blob ? new File([blob], "jotbay-week.png", { type: "image/png" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "JotBay", text, url });
        return;
      }
      if (navigator.share) { await navigator.share({ title: "JotBay", text, url }); return; }
      if (blob) {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = "jotbay-week.png"; a.click();
      }
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success(t("dashboard.recapDownloaded"));
    } catch { /* user dismissed the share sheet */ }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="weekly-recap">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-navy flex items-center gap-2"><CalendarRange className="h-4 w-4 text-[#202123]" /> {t("dashboard.thisWeek")}</h3>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-slatebody" data-testid="weekly-recap-range">{range}</span>
          <button onClick={shareRecap} data-testid="share-recap-btn"
            className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-navy hover:bg-slate-50 transition-colors">
            <Share2 className="h-3.5 w-3.5" /> {t("dashboard.shareMyWeek")}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div><p className="font-display text-2xl font-bold text-navy" data-testid="weekly-hours">{recap.total_hours}h</p><p className="text-[11px] text-slatebody">{t("dashboard.logged")}</p></div>
        <div><p className="font-display text-2xl font-bold text-navy" data-testid="weekly-entries">{recap.entry_count}</p><p className="text-[11px] text-slatebody">{t("dashboard.activities")}</p></div>
        <div><p className="font-display text-2xl font-bold text-navy" data-testid="weekly-active-days">{recap.active_days}/7</p><p className="text-[11px] text-slatebody">{t("dashboard.daysActive")}</p></div>
        <div><p className="font-display text-2xl font-bold text-navy flex items-center gap-1" data-testid="weekly-streak"><Flame className="h-4 w-4 text-orange-500" />{recap.streak}</p><p className="text-[11px] text-slatebody">{t("dashboard.dayStreak")}</p></div>
      </div>
      {recap.top_objectives && recap.top_objectives.length > 0 ? (
        <div className="pt-4 border-t border-slate-100">
          <p className="text-[11px] font-medium text-slatebody uppercase tracking-wide mb-2">{t("dashboard.topFocus")}</p>
          <div className="space-y-1.5" data-testid="weekly-top-objectives">
            {recap.top_objectives.map((o, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-navy truncate pr-3">{o.objective}</span>
                <span className="font-mono text-slatebody shrink-0">{o.hours}h</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slatebody pt-4 border-t border-slate-100">{t("dashboard.linkObjectivesHint")}</p>
      )}
    </div>
  );
}

function MilestonesRow({ streak }) {
  const { t } = useTranslation();
  if (!streak) return null;
  const milestones = streak.milestones || [7, 30, 100];
  const earned = streak.earned || [];
  const best = Math.max(streak.best || 0, streak.current || 0);
  const mlabel = (m) => (m === 7 ? t("dashboard.oneWeek") : m === 30 ? t("dashboard.oneMonth") : m === 100 ? t("dashboard.century") : t("dashboard.streakDays", { count: m }));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="milestones-row">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-navy flex items-center gap-2"><Flame className="h-4 w-4 text-orange-500" /> {t("dashboard.streakMilestones")}</h3>
        <span className="font-mono text-[11px] text-slatebody">{t("dashboard.earnedCount", { earned: earned.length, total: milestones.length })}</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {milestones.map((m) => {
          const isEarned = earned.includes(m);
          const pct = Math.min(100, Math.round((best / m) * 100));
          return (
            <div key={m} data-testid={`milestone-${m}`}
              className={`rounded-lg border p-4 text-center transition-colors ${isEarned ? "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" : "border-slate-200 bg-slate-50"}`}>
              <div className={`mx-auto flex h-11 w-11 items-center justify-center rounded-full mb-2 ${isEarned ? "bg-amber-100" : "bg-slate-200"}`}>
                {isEarned ? <Flame className="h-6 w-6 text-orange-500" /> : <Lock className="h-5 w-5 text-slate-400" />}
              </div>
              <p className={`font-display text-lg font-bold leading-none ${isEarned ? "text-navy" : "text-slate-400"}`}>{t("dashboard.daysLabel", { count: m })}</p>
              <p className="text-[11px] text-slatebody mt-1">{mlabel(m)}</p>
              {isEarned ? (
                <p className="text-[11px] font-medium text-[#202123] mt-2 flex items-center justify-center gap-1" data-testid={`milestone-${m}-earned`}><Check className="h-3 w-3" /> {t("dashboard.earned")}</p>
              ) : (
                <div className="mt-2" data-testid={`milestone-${m}-progress`}>
                  <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} /></div>
                  <p className="text-[10px] text-slatebody mt-1 font-mono">{best}/{m}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, suffix, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slatebody mb-2"><Icon className="h-4 w-4 text-[#202123]" /><span className="font-mono text-[11px] uppercase">{label}</span></div>
      <p className="font-display text-2xl font-bold text-navy">{value}<span className="text-sm font-normal text-slatebody">{suffix}</span></p>
    </div>
  );
}

function ChartCard({ title, children, empty }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="font-display font-semibold text-navy mb-4">{title}</h3>
      {empty ? <p className="text-sm text-slatebody py-8 text-center">No data for this period.</p>
        : <div style={{ width: "100%", height: 240 }}><ResponsiveContainer>{children}</ResponsiveContainer></div>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [scope, setScope] = useState("me");
  const [range, setRange] = useState({ start: daysAgo(90), end: iso(new Date()) });
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(null);
  const [recap, setRecap] = useState(null);
  const [exec, setExec] = useState(null);
  const [execBusy, setExecBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const res = await api.post("/dashboard/send-digest");
      if (res.data.email_configured) toast.success(t("dashboard.digestEmailed", { to: res.data.to, period: res.data.period }));
      else toast.success(t("dashboard.digestGenerated", { period: res.data.period }));
    } catch (err) { toast.error(apiError(err)); }
    finally { setDigestBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard", { params: { scope, start_date: range.start, end_date: range.end } });
      setData(res.data);
    } catch (err) { toast.error(apiError(err)); }
  }, [scope, range]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/logs/streak", { params: { local_date: localToday() } })
      .then((res) => setStreak(res.data)).catch(() => {});
    api.get("/logs/weekly-recap", { params: { local_date: localToday() } })
      .then((res) => setRecap(res.data)).catch(() => {});
  }, []);

  const genExec = async () => {
    setExecBusy(true);
    try {
      const res = await api.post("/dashboard/executive-summary", null, { params: { scope, start_date: range.start, end_date: range.end } });
      setExec(res.data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setExecBusy(false); }
  };

  const saveSummary = async () => {
    try {
      const title = `${t("dashboard.execTitle")} — ${exec.scope === "org" ? t("dashboard.scopeOrg") : t("dashboard.scopeTeam")} (${exec.period})`;
      const res = await api.post("/reports/from-summary", { title, content: exec.content });
      toast.success(t("dashboard.savedAsReport"));
      setExec(null);
      navigate("/app/reports", { state: { openReportId: res.data.id } });
    } catch (err) { toast.error(apiError(err)); }
  };

  const scopes = [{ v: "me", l: t("dashboard.scopeMe") }];
  if (data?.can_team) scopes.push({ v: "team", l: t("dashboard.scopeTeam") });
  if (data?.can_org) scopes.push({ v: "org", l: t("dashboard.scopeOrg") });

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy flex items-center gap-2"><LayoutDashboard className="h-7 w-7 text-[#202123]" /> {t("dashboard.title")}</h1>
          <p className="text-slatebody mt-1">{t("dashboard.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">{t("common.from")}</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="mt-1.5 font-mono h-9" data-testid="dash-start" /></div>
          <div><Label className="text-xs">{t("common.to")}</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="mt-1.5 font-mono h-9" data-testid="dash-end" /></div>
          {data && scope !== "me" && (
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5 h-9" disabled={execBusy} onClick={genExec} data-testid="exec-summary-btn">
              <Sparkles className="h-4 w-4" /> {execBusy ? t("common.generating") : t("dashboard.execSummary")}
            </Button>
          )}
          {data?.can_org && (
            <Button variant="outline" className="gap-1.5 h-9" disabled={digestBusy} onClick={sendDigest} data-testid="send-digest-btn">
              <Mail className="h-4 w-4" /> {digestBusy ? t("dashboard.sending") : t("dashboard.emailDigest")}
            </Button>
          )}
        </div>
      </div>

      {scopes.length > 1 && (
        <div className="flex gap-2 mb-6" data-testid="scope-selector">
          {scopes.map((s) => (
            <button key={s.v} onClick={() => setScope(s.v)} data-testid={`scope-${s.v}`}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${scope === s.v ? "border-[#C5C5D0] bg-accent text-accent-foreground" : "border-slate-300 text-slatebody hover:border-slate-400"}`}>
              {s.l}
            </button>
          ))}
        </div>
      )}

      {!data ? <p className="text-slatebody">{t("dashboard.loading")}</p> : (
        <>
          {data.entry_count === 0 && user?.account_type === "organization" && (
            <div className="rounded-xl border border-slate-200 bg-accent/40 p-5 mb-6" data-testid="dashboard-empty-msg">
              <p className="text-sm text-navy leading-relaxed max-w-2xl">
                {(user?.is_org_admin === true || user?.is_primary_admin === true) ? t("dashboard.emptyAdminMsg") : t("dashboard.emptyMemberMsg")}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat icon={Clock} label={t("dashboard.statTotalHours")} testid="stat-total-hours" value={data.total_hours} suffix="h" />
            <Stat icon={Activity} label={t("dashboard.statLogEntries")} testid="stat-log-entries" value={data.entry_count} />
            <Stat icon={Users} label={t("dashboard.statActivePeople")} testid="stat-active-people" value={data.active_people} suffix={data.scope !== "me" ? ` / ${data.member_count}` : ""} />
            <Stat icon={FileText} label={t("dashboard.statReports")} testid="stat-reports" value={data.report_count} />
          </div>

          {data.scope === "me" && <WeeklyRecap recap={recap} />}
          {data.scope === "me" && <MilestonesRow streak={streak} />}

          {data.at_risk_objectives && data.at_risk_objectives.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50/50 p-6 mb-6" data-testid="at-risk-panel">
              <h3 className="font-display font-semibold text-navy mb-1 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" /> {t("dashboard.atRisk")}</h3>
              <p className="text-xs text-slatebody mb-4">{t("dashboard.atRiskHint")}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.at_risk_objectives.map((o, i) => {
                  const badge = o.status === "overdue" ? { t: t("dashboard.overdue"), c: "bg-red-100 text-red-700" }
                    : o.status === "deadline_soon" ? { t: t("dashboard.deadlineSoon"), c: "bg-amber-100 text-amber-700" }
                    : { t: t("dashboard.behind"), c: "bg-amber-100 text-amber-700" };
                  return (
                    <div key={i} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`at-risk-${i}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${badge.c}`}>{badge.t}</span>
                        <span className="font-mono text-[11px] text-slatebody">{t("dashboard.hoursLogged", { h: o.hours })}</span>
                      </div>
                      <p className="font-medium text-navy text-sm leading-snug">{o.name}</p>
                      <p className="font-mono text-[11px] text-slatebody mt-1">{o.timeline} · {t("dashboard.due")} {o.deadline}{o.days_to_deadline < 0 ? ` (${t("dashboard.daysAgo", { n: -o.days_to_deadline })})` : ` (${t("dashboard.daysLeft", { n: o.days_to_deadline })})`}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className={`grid ${data.scope === "me" ? "lg:grid-cols-2" : "grid-cols-1"} gap-6 mb-6`}>
            <ChartCard title={t("dashboard.effortByObjective")} empty={data.by_objective.length === 0}>
              <BarChart data={data.by_objective} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11, fill: "#5C6672" }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "#202123" }} />
                <Tooltip cursor={{ fill: "#F5F6F9" }} />
                <Bar dataKey="hours" fill="#202123" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartCard>

            {data.scope === "me" && (
            <ChartCard title={t("dashboard.yourEffort")} empty={data.by_person.length === 0}>
              <BarChart data={data.by_person} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11, fill: "#5C6672" }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "#202123" }} />
                <Tooltip cursor={{ fill: "#F5F6F9" }} />
                <Bar dataKey="hours" fill="#202123" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartCard>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ChartCard title={t("dashboard.activityOverTime")} empty={data.activity_trend.length === 0}>
                <AreaChart data={data.activity_trend} margin={{ left: 0, right: 10, top: 5 }}>
                  <defs><linearGradient id="teal" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#202123" stopOpacity={0.3} /><stop offset="100%" stopColor="#202123" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#5C6672" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#5C6672" }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="hours" stroke="#202123" strokeWidth={2} fill="url(#teal)" />
                </AreaChart>
              </ChartCard>
            </div>

            {data.scope === "me" && (
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="inactive-objectives">
              <h3 className="font-display font-semibold text-navy mb-1 flex items-center gap-2"><Target className="h-4 w-4 text-[#202123]" /> {t("dashboard.noActivityObjectives")}</h3>
              <p className="text-xs text-slatebody mb-2">{t("dashboard.noActivityHint")}</p>
              <p className="text-[11px] text-slatebody mb-4 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5" data-testid="inactive-private-note"><Lock className="h-3 w-3" /> {t("dashboard.onlyYouSee")}</p>
              {data.inactive_objectives.length === 0
                ? <p className="text-sm text-slatebody">{t("dashboard.allSawActivity")} </p>
                : <ul className="space-y-2">{data.inactive_objectives.map((o, i) => (
                    <li key={i} className="text-sm text-navy flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2"><span className="h-1.5 w-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />{o}</li>
                  ))}</ul>}
            </div>
            )}
          </div>

          {data.plan_coverage && data.plan_coverage.length > 0 && (
            <div className="mt-6">
              <ChartCard title={t("dashboard.planVsActual")}>
                <BarChart data={data.plan_coverage} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#5C6672" }} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11, fill: "#202123" }} />
                  <Tooltip cursor={{ fill: "#F5F6F9" }} />
                  <Bar dataKey="hours" fill="#202123" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ChartCard>
            </div>
          )}
        </>
      )}

      <Dialog open={!!exec} onOpenChange={(o) => !o && setExec(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="exec-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Target className="h-5 w-5 text-[#202123]" /> {t("dashboard.execTitle")}</DialogTitle>
            <DialogDescription>{t("dashboard.execDialogDesc")} {exec?.period}</DialogDescription>
          </DialogHeader>
          {exec && (
            <>
              <MarkdownView content={exec.content} />
              <div className="pt-2 flex gap-2">
                <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" onClick={saveSummary} data-testid="exec-save"><FileText className="h-4 w-4" /> {t("dashboard.saveAsReport")}</Button>
                <Button variant="outline" className="gap-1.5" onClick={() => { navigator.clipboard.writeText(exec.content); toast.success(t("common.copied")); }} data-testid="exec-copy"><Copy className="h-4 w-4" /> {t("common.copy")}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
